import { useState } from "react";
import { match } from "ts-pattern";
import { t } from "ttag";

import {
  useRevertRevisionMutation,
  useUpdateDashboardMutation,
} from "metabase/api";
import type { DashboardUpdatedValue } from "metabase/api/ai-streaming/schemas";
import { getErrorMessage } from "metabase/api/utils/errors";
import { ForwardRefLink } from "metabase/common/components/Link";
import { useConfirmation } from "metabase/common/hooks/use-confirmation";
import { useToast } from "metabase/common/hooks/use-toast";
import type { MetabotDashboardUpdatedMessageProps } from "metabase/metabot";
import { useDispatch } from "metabase/redux";
import { Anchor, Box, Button, Flex, Group, Icon, Text } from "metabase/ui";
import * as Urls from "metabase/urls";

import { reloadOpenDashboard } from "./actions";

type UndoStatus = "idle" | "undoing" | "undone";

/**
 * The chat card for a dashboard Metabot created or changed: what changed, a link to the dashboard, and Undo, which
 * reverts the change or moves a created dashboard to the trash, after a confirmation.
 */
export function DashboardUpdatedMessage({
  value,
  readonly,
}: MetabotDashboardUpdatedMessageProps) {
  const dispatch = useDispatch();
  const [revertRevision] = useRevertRevisionMutation();
  const [updateDashboard] = useUpdateDashboardMutation();
  const [sendToast] = useToast();
  const { modalContent, show } = useConfirmation();
  const [status, setStatus] = useState<UndoStatus>("idle");

  const canUndo =
    !readonly &&
    status !== "undone" &&
    (value.created || value.revision_id != null);

  const undo = async () => {
    setStatus("undoing");
    try {
      await undoChange(value, { revertRevision, updateDashboard });
      await dispatch(reloadOpenDashboard(value.dashboard_id));
      setStatus("undone");
    } catch (error) {
      setStatus("idle");
      sendToast({
        icon: "warning",
        toastColor: "feedback-negative",
        message: getErrorMessage(
          error,
          t`Metabot's change couldn't be undone.`,
        ),
      });
    }
  };

  const confirmUndo = () =>
    show({
      title: value.created
        ? t`Move this dashboard to the trash?`
        : t`Undo Metabot's change?`,
      message: value.created
        ? t`“${value.name}” and the questions saved in it move to the trash. You can restore them from there.`
        : t`“${value.name}” goes back to how it was before this change. Changes made to it since then are undone too.`,
      confirmButtonText: value.created ? t`Move to trash` : t`Undo`,
      onConfirm: undo,
    });

  return (
    <Box
      bd="1px solid var(--mb-color-border-neutral)"
      bdrs="sm"
      p="md"
      data-testid="metabot-dashboard-updated"
    >
      <Flex align="center" gap="sm">
        <Icon name="dashboard" c="core-brand" />
        <Text fw="bold" truncate>
          {value.name}
        </Text>
      </Flex>
      <Text c="text-secondary" mt="xs">
        <StatusText created={value.created} status={status} />
      </Text>
      {status !== "undone" && value.summary && (
        <Text mt="xs">{value.summary}</Text>
      )}
      <Group gap="sm" mt="md">
        <Anchor
          component={ForwardRefLink}
          to={Urls.dashboard({ id: value.dashboard_id, name: value.name })}
          fw="bold"
        >
          {t`Open dashboard`}
        </Anchor>
        {canUndo && (
          <Button
            size="compact-sm"
            variant="subtle"
            leftSection={<Icon name="undo" />}
            loading={status === "undoing"}
            onClick={confirmUndo}
          >
            {t`Undo`}
          </Button>
        )}
      </Group>
      {modalContent}
    </Box>
  );
}

function StatusText({
  created,
  status,
}: {
  created: boolean;
  status: UndoStatus;
}) {
  return match({ created, undone: status === "undone" })
    .with({ created: true, undone: true }, () => t`Moved to the trash`)
    .with({ created: false, undone: true }, () => t`Change undone`)
    .with({ created: true, undone: false }, () => t`Dashboard created`)
    .with({ created: false, undone: false }, () => t`Dashboard changed`)
    .exhaustive();
}

type UndoMutations = {
  revertRevision: ReturnType<typeof useRevertRevisionMutation>[0];
  updateDashboard: ReturnType<typeof useUpdateDashboardMutation>[0];
};

async function undoChange(
  value: DashboardUpdatedValue,
  { revertRevision, updateDashboard }: UndoMutations,
) {
  if (value.created) {
    await updateDashboard({ id: value.dashboard_id, archived: true }).unwrap();
  } else if (value.revision_id != null) {
    await revertRevision({
      entity: "dashboard",
      id: value.dashboard_id,
      revision_id: value.revision_id,
    }).unwrap();
  }
}
